import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Loader2, ShieldCheck, Clock } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  pending_payment: { label: "Awaiting payment", color: "text-yellow-400" },
  pending_review: { label: "Pending Review", color: "text-amber-400" },
  reviewed: { label: "Reviewed & Co-signed", color: "text-emerald-400" },
};

export default function AdminReviews() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending_review");

  useEffect(() => {
    if (user && !user.is_admin) {
      toast.error("Admin access required");
      nav("/dashboard");
    }
  }, [user, nav]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/reviews", {
        params: { scope: "admin", status_filter: filter || undefined },
      });
      setItems(r.data);
    } catch (e) {
      toast.error("Failed to load review queue");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (user && !user.is_admin) return;   // non-admin: redirect effect above handles it
    load();
  }, [load, user]);

  return (
    <Layout>
      <div className="flex items-center gap-3">
        <ShieldCheck size={26} className="text-[var(--pa-gold)]" />
        <div>
          <div className="kicker">Specialist Review by Dr. Ronik Seecharan DMD, Prosthodontist — 30 years of clinical practice dedicated to treatment planning excellence</div>
          <h1 className="font-serif text-3xl md:text-4xl text-[var(--pa-text)] mt-1" data-testid="admin-reviews-title">
            Review Queue
          </h1>
        </div>
      </div>

      <div className="gold-divider my-8" />

      <div className="flex gap-2 mb-6">
        {[
          { id: "pending_review", label: "Pending" },
          { id: "reviewed", label: "Reviewed" },
          { id: "", label: "All" },
        ].map((t) => (
          <button
            key={t.id || "all"}
            onClick={() => setFilter(t.id)}
            data-testid={`admin-filter-${t.id || "all"}`}
            className={`text-sm px-4 py-2 rounded-md border transition ${
              filter === t.id
                ? "border-[var(--pa-gold)] bg-[var(--pa-gold)]/10 text-[var(--pa-gold)]"
                : "border-[var(--pa-border)] text-[var(--pa-text-muted)] hover:border-[var(--pa-border-active)]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-[var(--pa-text-muted)] flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-[var(--pa-border)] rounded-lg p-12 text-center text-sm text-[var(--pa-text-muted)]">
          No cases in this view.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--pa-border)] text-left">
                <th className="kicker pb-3 pr-4">Patient</th>
                <th className="kicker pb-3 pr-4">Module</th>
                <th className="kicker pb-3 pr-4">Submitted</th>
                <th className="kicker pb-3 pr-4">Status</th>
                <th className="kicker pb-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const meta = STATUS_META[r.status] || { label: r.status, color: "" };
                return (
                  <tr
                    key={r.id}
                    data-testid={`admin-review-row-${r.id}`}
                    className="border-b border-[var(--pa-border)] hover:bg-[var(--pa-surface)] transition"
                  >
                    <td className="py-4 pr-4 font-serif text-[var(--pa-text)]">
                      {r.patient_initials || "—"}
                    </td>
                    <td className="py-4 pr-4 text-sm text-[var(--pa-text-muted)]">{r.module_name}</td>
                    <td className="py-4 pr-4 text-xs text-[var(--pa-text-dim)]">
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                    <td className={`py-4 pr-4 text-xs font-semibold ${meta.color}`}>
                      <Clock size={12} className="inline -mt-0.5 mr-1" />
                      {meta.label}
                    </td>
                    <td className="py-4">
                      <Link
                        to={`/admin/reviews/${r.id}`}
                        data-testid={`admin-open-${r.id}`}
                        className="text-sm text-[var(--pa-gold)] hover:underline"
                      >
                        Open →
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Layout>
  );
}
